import { ChevronDown, ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { ContextMenuItem } from '@/components/ui/context-menu';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

import { usePartLabel, type PartResult } from './RunPartTags';

const OPEN_PART_KEYS = {
  cutFill: 'analyses.openPart.cutFill',
  deviation: 'analyses.openPart.deviation',
} as const;

/**
 * "Open result" of a paired comparison, as a split button: the main half
 * opens the first part that has a result layer (cut & fill first), the
 * chevron lists "Open cut & fill" / "Open deviation map", each disabled
 * without a layer. Renders nothing when neither part has one.
 */
export function ComparisonOpenResult({
  runId,
  parts,
  onOpenLayer,
}: {
  runId: string;
  parts: readonly PartResult[];
  onOpenLayer: (layerId: string) => void;
}) {
  const { t } = useTranslation('project');
  const partLabel = usePartLabel();
  const primary = parts.find((p) => p.layer);
  if (!primary?.layer) return null;
  const primaryLayer = primary.layer;
  return (
    <div role='group' aria-label={t('analysisLauncher.runs.openResult')} className='inline-flex items-center'>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant='ghost'
            size='icon'
            className='size-8 rounded-r-none'
            data-testid={`analysis-run-open-${runId}`}
            aria-label={`${t('analysisLauncher.runs.openResult')}: ${partLabel(primary.part)}`}
            onClick={() => onOpenLayer(primaryLayer.id)}>
            <ExternalLink className='size-4' aria-hidden />
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          {t('analysisLauncher.runs.openResult')} · {primaryLayer.name}
        </TooltipContent>
      </Tooltip>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button
            variant='ghost'
            size='icon'
            // Wider on touch: a 20 px target is too small for a finger.
            className='h-8 w-6 rounded-l-none sm:w-5'
            data-testid={`analysis-run-open-menu-${runId}`}
            aria-label={t('analyses.openResultMenu')}>
            <ChevronDown className='size-3.5' aria-hidden />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align='end' className='w-72'>
          {parts.map(({ part, layer }) => (
            <DropdownMenuItem
              key={part}
              data-testid={`analysis-run-open-${part}`}
              disabled={!layer}
              onSelect={() => layer && onOpenLayer(layer.id)}>
              <ExternalLink />
              <span className='shrink-0'>{t(OPEN_PART_KEYS[part])}</span>
              {layer && (
                <span className='min-w-0 flex-1 truncate text-right text-xs text-muted-foreground' title={layer.name}>
                  {layer.name}
                </span>
              )}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

/** The same choices as context-menu items, disabled without a layer. */
export function ComparisonOpenResultItems({
  parts,
  onOpenLayer,
}: {
  parts: readonly PartResult[];
  onOpenLayer: (layerId: string) => void;
}) {
  const { t } = useTranslation('project');
  return (
    <>
      {parts.map(({ part, layer }) => (
        <ContextMenuItem
          key={part}
          data-testid={`analysis-run-context-open-result-${part}`}
          disabled={!layer}
          onSelect={() => layer && onOpenLayer(layer.id)}>
          <ExternalLink />
          {t(OPEN_PART_KEYS[part])}
        </ContextMenuItem>
      ))}
    </>
  );
}
